import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select } from '@mantine/core';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import moment from 'moment';
import 'moment/locale/es';

import { getConsumptionsSummary } from '../../../../../slices/thunks';
import OChartCard from '../../../../../Components/OElements/OChartCard';
import OBarChart from '../../../../../Components/OElements/OBarChart';

moment.locale('es');

// ─── Helpers ────────────────────────────────────────────────────────────

const formatMap = {
	'2A': 'YYYY',
	'1A': 'YYYY',
	'6M': 'MM-YYYY',
	'3M': 'MM-YYYY',
	'1M': 'YYYY-[S]WW',
	'7D': 'DD-MM-YYYY',
	AC: 'MM-YYYY',
};

const formatDate = (date, filter) => {
	return moment(date).format(formatMap[filter] || 'DD-MM-YYYY');
};

// const getUniqueUnits = data => {
// 	if (!Array.isArray(data) || data.length === 0) return 'N/A';
// 	const uniqueUnits = new Set(
// 		data
// 			.map(item => 'kgCO2')
// 			.filter(Boolean)
// 			.map(unit => unit.charAt(0).toUpperCase() + unit.slice(1))
// 	);
// 	return uniqueUnits.size > 0 ? Array.from(uniqueUnits).join(', ') : 'N/A';
// };

const ConsumptionsPowerGenerationEChart = ({
	establishmentId,
	title,
	legendPosition = 'bottom',
	defaultFilters = {
		option: 'dia',
		group: 'centro',
	},
	um = '',
	minHeight,
	maxHeight,
}) => {
	const dispatch = useDispatch();
	const [selectedDate, setSelectedDate] = useState(defaultFilters.option);
	const [selectedGroup, setSelectedGroup] = useState(defaultFilters.group);

	const selectorEmissions = createSelector(
		state => state.FuelSupply,
		(_, type) => type,
		(data, type) => ({ info: data.consumptionsSummary || null })
	);
	const { info: infoConsumptions } = useSelector(state =>
		selectorEmissions(state, 'consumptions')
	);

	const fetchEmissionsData = useCallback(
		(idEstablishment, filter, group) => {
			dispatch(
				getConsumptionsSummary({
					establishment_id: idEstablishment,
					filter: filter,
					group: group,
				})
			);
		},
		[dispatch]
	);

	// Fetch data
	useEffect(() => {
		if (!establishmentId) return;
		fetchEmissionsData(establishmentId, selectedDate, selectedGroup);
	}, [establishmentId, selectedDate, selectedGroup, fetchEmissionsData]);

	const { series, categories, legendData } = useMemo(() => {
		if (!infoConsumptions?.data)
			return { series: [], categories: [], legendData: [] };

		// Get unique fuel types from all dates
		const fuelTypes = [
			...new Set(
				infoConsumptions.data
					.flatMap(item => item.fuel_types?.map(fuel => fuel.name) || [])
					.filter(Boolean)
			),
		];

		// Create series for each fuel type
		const seriesData = fuelTypes.map(fuelType => ({
			name: fuelType,
			type: 'bar',
			stack: 'total',
			data: infoConsumptions.data.map(item => {
				const fuelTypeData = item.fuel_types?.find(
					fuel => fuel.name === fuelType
				);
				return fuelTypeData ? fuelTypeData.consumption : 0;
			}),
		}));

		return {
			series: seriesData,
			categories: infoConsumptions.data.map(item => item.date),
			legendData: fuelTypes,
		};
	}, [infoConsumptions?.data]);

	// const yAxisUnit = useMemo(() => {
	// 	return getUniqueUnits(infoEmissions?.data);
	// }, [infoEmissions?.data]);

	const options = useMemo(
		() => ({
			tooltip: {
				trigger: 'axis',
				axisPointer: { type: 'shadow' },
				formatter: function (params) {
					let result = params[0].axisValue + '<br/>';
					let total = 0;
					params.forEach(param => {
						if (param.value > 0) {
							const dateData = infoConsumptions.data.find(
								item => item.date === params[0].axisValue
							);
							const fuelTypeData = dateData?.fuel_types?.find(
								fuel => fuel.name === param.seriesName
							);
							result +=
								param.marker +
								' ' +
								param.seriesName +
								': ' +
								param.value +
								' ' +
								(fuelTypeData?.unit_measurement || 'Litros') +
								'<br/>';
							total += param.value;
						}
					});
					result += '<b>Total: ' + total + ' Litros</b>';
					return result;
				},
			},
			legend: {
				data: legendData,
				orient: 'horizontal',
				bottom: 10,
				type: 'scroll',
			},
			xAxis: [{ type: 'category', data: categories }],
			yAxis: [{ type: 'value', name: 'Litros' }],
			series: series,
		}),
		[series, categories, legendData]
	);

	const noData = !infoConsumptions?.data?.length;
	const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
	useEffect(() => {
		if (Array.isArray(infoConsumptions?.data)) {
			setHasLoadedOnce(true);
		}
	}, [infoConsumptions?.data]);

	const actions = (
		<div className='btn-box-dashboard' style={{ display: 'flex', gap: 8 }}>
			<Select
				placeholder='Agrupación'
				data={[
					{ label: 'Por Centro', value: 'centro' },
					{ label: 'Por Fuente', value: 'fuente' },
				]}
				value={selectedGroup}
				onChange={value => value && setSelectedGroup(value)}
				size='xs'
				radius='lg'
				styles={{ input: { width: 100 } }}
			/>
			<Select
				placeholder='Fecha'
				data={[
					{ label: 'Ciclo', value: 'ciclo' },
					{ label: 'Año', value: 'año' },
					{ label: 'Semestre', value: 'semestre' },
					{ label: 'Mes', value: 'mes' },
					{ label: 'Día', value: 'dia' },
				]}
				value={selectedDate}
				onChange={value => value && setSelectedDate(value)}
				size='xs'
				radius='lg'
				styles={{ input: { width: 100 } }}
			/>
		</div>
	);

	return (
		<OChartCard
			title={title}
			actions={actions}
			isLoading={!hasLoadedOnce && !infoConsumptions?.data}
			error={null}
			isEmpty={noData}
			emptyIconClass='ri-gas-station-fill'
			emptyMessage='No cuenta con registro de consumo de combustible'
		>
			<OBarChart
				options={options}
				minHeight={minHeight}
				maxHeight={maxHeight}
			/>
		</OChartCard>
	);
};

export default ConsumptionsPowerGenerationEChart;
